import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Minus } from 'lucide-react';

export default function FAQSection() {
  const { t } = useLanguage();
  const items = t('faq.items') || [];
  const [openIdx, setOpenIdx] = useState(0);

  return (
    <section id="faq" data-testid="faq-section" className="bg-white py-24 md:py-32">
      <div className="max-w-[900px] mx-auto px-6 md:px-10">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
          className="text-[#befa0a] text-xs uppercase tracking-[0.3em] mb-4 font-bold"
          style={{ fontFamily: "'Oswald', sans-serif" }}
        >
          {t('faq.label')}
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="text-[#1f1f1f] text-3xl md:text-4xl font-bold uppercase tracking-tight leading-[1.3] mb-4"
          style={{ fontFamily: "'Oswald', sans-serif" }}
        >
          {t('faq.title')}
        </motion.h2>
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="text-[#1f1f1f]/60 text-base md:text-lg mb-12 max-w-2xl"
          style={{ fontFamily: "'Manrope', sans-serif" }}
        >
          {t('faq.subtitle')}
        </motion.p>

        <div className="border-t border-[#e5e5e5]">
          {Array.isArray(items) && items.map((item, i) => {
            const isOpen = openIdx === i;
            return (
              <div key={i} data-testid={`faq-item-${i}`} className="border-b border-[#e5e5e5]">
                <button
                  onClick={() => setOpenIdx(isOpen ? -1 : i)}
                  aria-expanded={isOpen}
                  className="w-full flex items-start justify-between gap-6 py-6 text-left group"
                >
                  <h3
                    className="text-[#1f1f1f] text-base md:text-lg font-bold uppercase tracking-tight leading-snug group-hover:text-[#658a07] transition-colors flex-1"
                    style={{ fontFamily: "'Oswald', sans-serif" }}
                  >
                    {item.question}
                  </h3>
                  <span className="text-[#1f1f1f] flex-shrink-0 mt-1">
                    {isOpen ? <Minus size={18} /> : <Plus size={18} />}
                  </span>
                </button>
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                      className="overflow-hidden"
                    >
                      <p
                        className="text-[#1f1f1f]/70 text-base leading-relaxed pb-6 pr-12"
                        style={{ fontFamily: "'Manrope', sans-serif" }}
                      >
                        {item.answer}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
